import type { ProductListItem } from '@travel-planner/shared/schemas';
import type {
  AdvisorGuests,
  AdvisorProduct,
  AdvisorRegion,
} from '../model/types';
import type { DayTarget, ToHotel } from '../model/v3-results';
import type { ChapterStay } from './planner-v3-add-action';
import { useDraggable } from '@dnd-kit/core';
import { useHoveredMarkerStore } from '@map-ui';
import {
  hotelPriceLevel,
  usableAvgRoomNight,
} from '@travel-planner/shared/utils/hotel-avg-room-night';
import { ChevronDown, GripVertical } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import {
  awardTags,
  briefMatches,
  briefPrice,
  cardDuration,
  guestsLabel,
  hotelSetting,
  money,
  partyTotal,
  restaurantFormat,
  servicePeriod,
} from '../model/card-facts';
import { advisorSourceDragId } from '../model/dnd';
import {
  priceLevelRange,
  priceLevelSymbol,
  starsLabel,
} from '../model/hotel-class';
import { regionName } from '../model/region-layout';
import { formatKm, placeLine } from '../model/v3-results';
import { useOptionalPlannerHost } from './planner-host';
import { PlannerV3AddAction } from './planner-v3-add-action';
import { PlannerV3CardDetails } from './planner-v3-card-details';
import { OnRequestPrice } from './planner-v3-price';
import css from './planner-v3-results.module.scss';
import { UsdEstimate } from './planner-v3-usd';
import { useCommissionOnTop } from './use-commission-on-top';

const cardCss = pickCss(css, css.card);
const expandCss = pickCss(css, css.expand);
const thumbCss = pickCss(css, css.thumb);

/** The nearest ancestor that scrolls vertically (the results list). */
const scrollParent = (node: HTMLElement): HTMLElement | null => {
  for (let el = node.parentElement; el; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      el.scrollHeight > el.clientHeight
    ) {
      return el;
    }
  }
  return null;
};

const initialsOf = (title: string) =>
  title
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

const NO_PREVIEW = () => ({
  data: undefined,
  isLoading: false,
  isError: false,
});

const NO_TO_CENTRE = () => ({
  data: undefined,
  isLoading: false,
  isError: false,
});

type PlannerV3ResultCardProps = {
  item: ProductListItem;
  product: AdvisorProduct;
  inItinerary: boolean;
  /** This hotel is a chapter's base hotel. */
  isBaseHotel: boolean;
  hotel: ToHotel;
  expanded: boolean;
  days: DayTarget[];
  onToggle: () => void;
  onAdd: () => void;
  /** `region`: the agent's pick for a hotel with no region; `nights`: its stay. */
  onSetBaseHotel: (region?: AdvisorRegion, nights?: number) => void;
  /** The stay of a region's chapter, to start a hotel's nights from. */
  stayFor?: (regionId: string) => ChapterStay;
  onAddToDay: (dayId: string) => void;
  /** A new day at the end of the product's (or the picked) region chapter. */
  onAddToNewDay: (region?: AdvisorRegion) => void;
  /** The country's regions, offered when the product has no region. */
  regions?: readonly AdvisorRegion[];
  /** The trip has days in other regions, which Add leaves out. */
  otherRegionDays?: boolean;
  /**
   * Show the hotel's sales history (average sell price, room nights sold).
   * Off by default; hosts opt in.
   */
  showAvgRoomNight?: boolean;
  /** The brief's party and rooms. */
  party?: AdvisorGuests;
  rooms?: number;
  /** The brief's moods: a hashtag among them reads "Matches brief". */
  briefMoods?: readonly string[];
  /** The travellers this product is booked for; null for everyone. */
  guests?: AdvisorGuests | null;
  onGuestsChange?: (guests: AdvisorGuests | null) => void;
};

/**
 * A v5 result card (XT-840): one row — photo; name, place and tags, a line
 * about it, its facts; the price for this brief and the action. The chevron,
 * title or photo opens the card in place.
 */
export const PlannerV3ResultCard = ({
  item,
  product,
  inItinerary,
  isBaseHotel,
  hotel,
  expanded,
  days,
  onToggle,
  onAdd,
  onSetBaseHotel,
  onAddToDay,
  onAddToNewDay,
  regions = [],
  stayFor,
  otherRegionDays = false,
  showAvgRoomNight = false,
  party = { adults: 2, children: 0 },
  rooms = 1,
  briefMoods = [],
  guests = null,
  onGuestsChange,
}: PlannerV3ResultCardProps) => {
  const setHovered = useHoveredMarkerStore((s) => s.setHovered);
  // Its marker hovered on the map: the card takes the hover look and, when
  // out of view, the results list scrolls to it.
  const hoveredOnMap = useHoveredMarkerStore(
    (s) => s.hoveredSource === 'map' && s.hoveredId === product.id
  );
  const cardRef = useRef<HTMLElement | null>(null);
  const chevronRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const card = cardRef.current;
    if (!hoveredOnMap || !card) return;
    const list = scrollParent(card);
    if (!list) return;
    const top = card.offsetTop - list.offsetTop;
    const bottom = top + card.offsetHeight;
    if (top < list.scrollTop || bottom > list.scrollTop + list.clientHeight) {
      list.scrollTo({ top: Math.max(0, top - 12), behavior: 'smooth' });
    }
  }, [hoveredOnMap]);
  const host = useOptionalPlannerHost();
  const usePreview = host?.useProductPreview ?? NO_PREVIEW;
  const preview = usePreview(expanded ? product.id : null);
  const isHotel = product.kind === 'hotel';
  const useToCentre = host?.useToCentre ?? NO_TO_CENTRE;
  const toCentre = useToCentre(
    expanded && isHotel && product.location && item.city
      ? {
          lat: product.location.lat,
          lng: product.location.lng,
          city: item.city,
          country: item.country ?? undefined,
        }
      : null
  );
  // A hotel is a chapter's base hotel, set from here; anything else goes on
  // a day once.
  const done = isHotel ? isBaseHotel : inItinerary;
  const draggable = useDraggable({
    id: advisorSourceDragId(product.id),
    data: {
      kind: 'source-product',
      productId: product.id,
      regionId: product.regionId,
      title: product.title,
      thumbnail: product.thumbnail,
    },
    disabled: done || isHotel || expanded,
  });
  const place = placeLine(
    item.city ?? item.country,
    product.regionTitle,
    regionName
  );
  const booked = guests ?? party;
  const stayNights =
    isHotel && product.regionId && stayFor
      ? stayFor(product.regionId).nights
      : 1;
  // T-405: the portal's prices carry the agency's commission on top.
  const priced = useCommissionOnTop().item(product);
  const price = briefPrice(priced, booked, { rooms, nights: stayNights });
  const km = hotel.kind === 'km' && !isHotel ? formatKm(hotel.km) : null;
  const matches = briefMatches(product.tags, briefMoods);
  const awards = product.kind === 'restaurant' ? awardTags(product.tags) : [];
  const average =
    isHotel && showAvgRoomNight
      ? usableAvgRoomNight(priced.avgRoomNight)
      : null;
  const roomNights =
    isHotel && showAvgRoomNight ? (product.avgRoomNight?.roomNights ?? 0) : 0;
  const stars = isHotel ? (product.stars ?? null) : null;
  const priceLevel =
    isHotel && showAvgRoomNight ? hotelPriceLevel(product.avgRoomNight) : null;

  // The facts line, most telling first; each truncates rather than wraps.
  const facts: Array<{ text: string; strong?: boolean }> = [];
  if (product.kind === 'hotel') {
    const setting = hotelSetting(product.tags);
    if (setting.length > 0)
      facts.push({ text: setting.join(' · '), strong: true });
    if (roomNights > 0) facts.push({ text: `${roomNights} room nights sold` });
  } else if (product.kind === 'experience') {
    const duration = cardDuration(product.kind, product.durationMinutes);
    if (duration) facts.push({ text: duration, strong: true });
    if (km) facts.push({ text: `${km} from hotel` });
  } else {
    const service = servicePeriod(product.startMinutes);
    if (service) facts.push({ text: service, strong: true });
    const format = restaurantFormat(product.tags);
    if (format) facts.push({ text: format });
    if (km) facts.push({ text: `${km} from hotel` });
  }
  const bookedFor = isHotel ? null : guestsLabel(guests, party);
  const inTripLabel =
    isHotel && isBaseHotel
      ? `In itinerary · ${stayNights} ${stayNights === 1 ? 'night' : 'nights'}`
      : inItinerary
        ? 'In itinerary'
        : null;

  const collapse = () => {
    onToggle();
    cardRef.current?.scrollIntoView?.({ block: 'nearest' });
    chevronRef.current?.focus();
  };

  return (
    <article
      aria-label={product.title}
      className={cardCss({
        isExpanded: expanded,
        isAdded: inItinerary,
        isDragging: draggable.isDragging,
        isMapHover: hoveredOnMap,
        isDraggable: !done && !isHotel && !expanded,
      })}
      data-product-id={product.id}
      onKeyDown={(event) => {
        if (expanded && event.key === 'Escape') {
          event.stopPropagation();
          collapse();
        }
      }}
      onMouseEnter={() => setHovered(product.id)}
      onMouseLeave={() => setHovered(null)}
      ref={(node) => {
        cardRef.current = node;
        draggable.setNodeRef(node);
      }}
      {...draggable.listeners}
    >
      <div className={css.main}>
        {!done && !isHotel && !expanded ? (
          <span aria-hidden="true" className={css.grip} title="Drag onto a day">
            <GripVertical size={14} />
          </span>
        ) : null}
        <button
          aria-hidden="true"
          className={thumbCss({ isHotel })}
          onClick={onToggle}
          tabIndex={-1}
          type="button"
        >
          {product.thumbnail ? (
            <img
              alt=""
              className={css.thumbImg}
              loading="lazy"
              src={product.thumbnail}
            />
          ) : (
            <span className={css.initials}>{initialsOf(product.title)}</span>
          )}
        </button>
        <div className={css.info}>
          <button
            aria-expanded={expanded}
            className={css.titleButton}
            onClick={onToggle}
            type="button"
          >
            <span className={css.title}>{product.title}</span>
            {stars !== null ? (
              <span
                aria-label={`${stars} stars`}
                className={css.stars}
                role="img"
                title={`${stars} stars`}
              >
                {starsLabel(stars)}
              </span>
            ) : null}
            {priceLevel !== null ? (
              <span
                aria-label={`Price level ${priceLevelSymbol(priceLevel)}, ${priceLevelRange(priceLevel)} a night`}
                className={css.priceLevel}
                role="img"
                title={`Sold ${priceLevelRange(priceLevel)} a night on average`}
              >
                {priceLevelSymbol(priceLevel)}
              </span>
            ) : null}
            {inTripLabel ? (
              <span className={css.pill}>{inTripLabel}</span>
            ) : null}
          </button>
          <span className={css.meta}>
            {place ? <span className={css.place}>{place}</span> : null}
            {/* The first booking condition; the rest show when opened. */}
            {(product.conditions ?? []).slice(0, 1).map((condition) => (
              <span className={css.tagWarn} key={condition}>
                {condition}
              </span>
            ))}
            {awards.slice(0, 1).map((award) => (
              <span className={css.tagAward} key={award}>
                {award}
              </span>
            ))}
            {matches.slice(0, 1).map((mood) => (
              <span className={css.tagMatch} key={mood}>
                Matches brief: {mood}
              </span>
            ))}
          </span>
          {product.strapline ? (
            <span className={css.strapline}>{product.strapline}</span>
          ) : null}
          {facts.length > 0 ? (
            <span className={css.facts}>
              {facts.map((fact) => (
                <span
                  className={fact.strong ? css.factStrong : css.fact}
                  key={fact.text}
                >
                  {fact.text}
                </span>
              ))}
            </span>
          ) : null}
        </div>
        <div className={css.side}>
          <div className={css.price}>
            {price.mode === 'request' ? (
              <>
                <span className={css.priceMain}>
                  <OnRequestPrice text="On request" />
                </span>
                {average !== null ? (
                  <span className={css.priceSub}>
                    sold ≈ {money(average)} / night avg
                    <UsdEstimate amount={average} currency="EUR" />
                  </span>
                ) : null}
              </>
            ) : price.mode === 'night' ? (
              <>
                <span className={css.priceMain}>
                  {money(price.perNight * price.rooms, price.currency)}
                  <UsdEstimate
                    amount={price.perNight * price.rooms}
                    currency={price.currency}
                  />
                </span>
                <span className={css.priceSub}>
                  per night · {price.rooms}{' '}
                  {price.rooms === 1 ? 'room' : 'rooms'}
                </span>
              </>
            ) : price.mode === 'pax' ? (
              <>
                <span className={css.priceMain}>
                  {money(price.total, price.currency)}
                  <UsdEstimate amount={price.total} currency={price.currency} />
                </span>
                <span className={css.priceSub}>
                  {money(price.perPerson, price.currency)} per person
                  <UsdEstimate
                    amount={price.perPerson}
                    currency={price.currency}
                  />
                </span>
              </>
            ) : (
              <>
                <span className={css.priceMain}>
                  {money(price.total, price.currency)}
                  <UsdEstimate amount={price.total} currency={price.currency} />
                </span>
                <span className={css.priceSub}>for the group</span>
              </>
            )}
            {bookedFor ? (
              <span className={css.bookedFor}>{bookedFor}</span>
            ) : null}
          </div>
          <div className={css.sideActions}>
            <button
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Less' : 'More'} about ${product.title}`}
              className={expandCss({ isOpen: expanded })}
              onClick={onToggle}
              ref={chevronRef}
              type="button"
            >
              <ChevronDown aria-hidden="true" size={15} />
            </button>
            <PlannerV3AddAction
              days={days}
              inItinerary={inItinerary}
              isBaseHotel={isBaseHotel}
              onAdd={onAdd}
              onAddToDay={onAddToDay}
              onAddToNewDay={onAddToNewDay}
              onSetBaseHotel={onSetBaseHotel}
              product={product}
              otherRegionDays={otherRegionDays}
              regions={regions}
              stayFor={stayFor}
            />
          </div>
        </div>
      </div>
      {expanded ? (
        <PlannerV3CardDetails
          guests={booked}
          isError={preview.isError}
          isLoading={preview.isLoading}
          onCollapse={collapse}
          onGuestsChange={(next) =>
            onGuestsChange?.(
              partyTotal(next) >= partyTotal(party) &&
                next.adults >= party.adults
                ? null
                : next
            )
          }
          party={party}
          preview={preview.data}
          price={price}
          product={priced}
          showAvgRoomNight={showAvgRoomNight}
          toCentre={host?.useToCentre ? toCentre : undefined}
          toHotel={km}
        />
      ) : null}
    </article>
  );
};
